import React, {
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from 'react';
import {
  client,
  getPatientDocuments,
  getPatientDocumentUrl,
  uploadPatientDocument,
  updatePatientDocument,
  updateIntakeDocument,
  getIntakes,
  getReferralDocs,
} from '../../services/api';
import PDFViewerNative from '../PDFViewerNative';
import type {
  PatientDocumentResponse,
  IntakeDocumentResponse,
} from '../../services/api/types.gen';
import { useToast } from '../../hooks/useToastContext';

interface IntakeDocumentWithMeta extends IntakeDocumentResponse {
  intakeId: string;
}

// Human-readable label for the `task_type` polymorphic discriminator
// (values: "visit", "order", "document_upload", "task").
const TASK_TYPE_LABELS: Record<string, string> = {
  visit: 'Visit',
  order: 'Order',
  document_upload: 'Document upload',
  task: 'Task',
};

const formatTaskTag = (doc: PatientDocumentResponse): string | null => {
  if (!doc.task_id) return null;
  const typeLabel = doc.task_type
    ? (TASK_TYPE_LABELS[doc.task_type] ?? doc.task_type)
    : 'Task';
  return doc.task_scheduled_date
    ? `[${typeLabel} ${doc.task_scheduled_date}]`
    : `[${typeLabel}]`;
};

const LoadingSpinner: React.FC<{ size?: 'sm' | 'md'; text?: string }> = ({
  size = 'md',
  text,
}) => (
  <div className='flex items-center gap-2'>
    <div
      className={`animate-spin rounded-full border-2 border-primary border-t-transparent ${size === 'sm' ? 'h-4 w-4' : 'h-8 w-8'}`}
    />
    {text && <span className='text-sm text-muted-foreground'>{text}</span>}
  </div>
);

export interface PatientDocumentsTabHandle {
  refreshDocuments: () => void;
}

interface PatientDocumentsTabProps {
  patientId: string;
}

const PatientDocumentsTab = forwardRef<
  PatientDocumentsTabHandle,
  PatientDocumentsTabProps
>(({ patientId }, ref) => {
  const { success: showSuccess, error: showError } = useToast();

  const [patientDocuments, setPatientDocuments] = useState<
    PatientDocumentResponse[]
  >([]);
  const [intakeDocuments, setIntakeDocuments] = useState<
    IntakeDocumentWithMeta[]
  >([]);
  const [patientDocsLoading, setPatientDocsLoading] = useState(false);
  const [intakeDocsLoading, setIntakeDocsLoading] = useState(false);
  const [activeDocumentId, setActiveDocumentId] = useState<string>('');
  const [documentUrls, setDocumentUrls] = useState<Record<string, string>>({});
  // ── Fetch patient documents ──────────────────────────────────────

  const fetchPatientDocuments = useCallback(async () => {
    setPatientDocsLoading(true);
    try {
      const response = await getPatientDocuments({
        path: { patient_id: patientId },
        query: { include_task_documents: true },
        client,
      });
      if (response?.data) {
        // Exclude intake-type docs — those are already shown via the intake relationship
        const docs = response.data.filter(d => d.document_type !== 'intake');
        setPatientDocuments(docs);
        const urlMap: Record<string, string> = {};
        // TODO: If this list grows large, switch to lazy URL signing (on access)
        // and add pagination so we do not eagerly fetch every document URL upfront.
        for (const doc of docs) {
          try {
            const urlResponse = await getPatientDocumentUrl({
              path: { patient_id: patientId, document_id: doc.id },
              client,
            });
            const urlData = urlResponse?.data as { url?: string } | undefined;
            if (urlData?.url) {
              urlMap[doc.id] = urlData.url;
            }
          } catch (err) {
            console.error(`Error fetching URL for document ${doc.id}:`, err);
            showError(
              'Document Error',
              'There was an error fetching the document.'
            );
          }
        }
        setDocumentUrls(prev => ({ ...prev, ...urlMap }));
      }
    } catch (err) {
      console.error('Failed to load patient documents:', err);
    } finally {
      setPatientDocsLoading(false);
    }
  }, [patientId]);

  useImperativeHandle(ref, () => ({
    refreshDocuments: fetchPatientDocuments,
  }));

  useEffect(() => {
    fetchPatientDocuments();
  }, [fetchPatientDocuments]);

  // ── Fetch intake documents ───────────────────────────────────────

  useEffect(() => {
    setIntakeDocsLoading(true);
    getIntakes({
      query: { patient_id: patientId },
      client,
    })
      .then(async response => {
        if (response?.data?.items) {
          const allIntakeDocs: IntakeDocumentWithMeta[] = [];
          const urlMap: Record<string, string> = {};

          for (const intake of response.data.items) {
            if (intake.documents && intake.documents.length > 0) {
              // Include intakeId for URL refresh to work correctly
              // Exclude documents tagged for orders/tasks — those belong to their respective task
              const docsWithMeta = intake.documents
                .filter(doc => !doc.task_type_tag)
                .map(doc => ({
                  ...doc,
                  intakeId: intake.id,
                }));
              allIntakeDocs.push(...docsWithMeta);

              try {
                const docsResponse = await getReferralDocs({
                  path: { intake_id: intake.id },
                  client,
                });
                if (docsResponse?.data) {
                  const docsData = docsResponse.data as {
                    documents?: Array<{ id: string; url: string }>;
                  };
                  if (docsData.documents) {
                    for (const docUrl of docsData.documents) {
                      urlMap[docUrl.id] = docUrl.url;
                    }
                  }
                }
              } catch (err) {
                console.error(
                  `Error fetching URLs for intake ${intake.id}:`,
                  err
                );
              }
            }
          }

          setIntakeDocuments(allIntakeDocs);
          setDocumentUrls(prev => ({ ...prev, ...urlMap }));
        }
      })
      .catch(err => console.error('Failed to load intake documents:', err))
      .finally(() => setIntakeDocsLoading(false));
  }, [patientId]);

  // ── Auto-select first document ───────────────────────────────────

  useEffect(() => {
    const allDocs = [...intakeDocuments, ...patientDocuments];
    if (allDocs.length > 0 && !activeDocumentId) {
      setActiveDocumentId(allDocs[0].id);
    }
  }, [intakeDocuments, patientDocuments, activeDocumentId]);

  // ── Handlers ─────────────────────────────────────────────────────

  const handleDocumentRename = async (documentId: string, fileName: string) => {
    const isPatientDoc = patientDocuments.some(d => d.id === documentId);

    try {
      if (isPatientDoc) {
        await updatePatientDocument({
          path: { patient_id: patientId, document_id: documentId },
          body: { file_name: fileName },
          client,
        });
        setPatientDocuments(prev =>
          prev.map(doc =>
            doc.id === documentId ? { ...doc, file_name: fileName } : doc
          )
        );
      } else {
        await updateIntakeDocument({
          path: { document_id: documentId },
          body: { file_name: fileName },
          client,
        });
        setIntakeDocuments(prev =>
          prev.map(doc =>
            doc.id === documentId ? { ...doc, file_name: fileName } : doc
          )
        );
      }
      showSuccess('Document Renamed', 'Document renamed successfully.');
    } catch (err) {
      console.error('Error renaming document:', err);
      showError('Rename Failed', 'Failed to rename document.');
    }
  };

  const handleDocumentAdd = async (file: File, displayName: string) => {
    try {
      const response = await uploadPatientDocument({
        path: { patient_id: patientId },
        query: { display_name: displayName },
        body: { file },
        client,
      });

      if (response.data) {
        setPatientDocuments(prev => [...prev, response.data!]);

        try {
          const urlResponse = await getPatientDocumentUrl({
            path: { patient_id: patientId, document_id: response.data.id },
            client,
          });
          const urlData = urlResponse?.data as { url?: string } | undefined;
          if (urlData?.url) {
            const url = urlData.url;
            setDocumentUrls(prev => ({
              ...prev,
              [response.data!.id]: url,
            }));
          }
        } catch (urlErr) {
          console.error('Error fetching URL for new document:', urlErr);
        }

        return {
          id: response.data.id,
          fileName: response.data.file_name,
        };
      }
      return null;
    } catch (err) {
      console.error('Error uploading document:', err);
      showError('Upload Failed', 'Failed to upload document.');
      return null;
    }
  };

  const handleRefreshDocumentUrl = useCallback(
    async (documentId: string): Promise<string | null> => {
      // Check if this is an intake document
      const intakeDoc = intakeDocuments.find(d => d.id === documentId);

      try {
        if (intakeDoc) {
          // Intake document - use getReferralDocs API
          const docsResponse = await getReferralDocs({
            path: { intake_id: intakeDoc.intakeId },
            client,
          });
          const docsData = docsResponse.data as {
            documents?: Array<{ id: string; url: string }>;
          };
          if (docsData?.documents) {
            const doc = docsData.documents.find(d => d.id === documentId);
            if (doc?.url) {
              setDocumentUrls(prev => ({ ...prev, [documentId]: doc.url }));
              return doc.url;
            }
          }
          return null;
        } else {
          // Patient document - use getPatientDocumentUrl API
          const urlResponse = await getPatientDocumentUrl({
            path: { patient_id: patientId, document_id: documentId },
            client,
          });
          const urlData = urlResponse?.data as { url?: string } | undefined;
          if (urlData?.url) {
            setDocumentUrls(prev => ({ ...prev, [documentId]: urlData.url! }));
            return urlData.url;
          }
          return null;
        }
      } catch (err) {
        console.error(`Error refreshing URL for document ${documentId}:`, err);
        return null;
      }
    },
    [patientId, intakeDocuments]
  );

  // ── Render ───────────────────────────────────────────────────────

  return (
    <div className='overflow-hidden rounded-lg border bg-card'>
      <div className='flex items-center px-4 py-2 border-b bg-muted/30'>
        {(intakeDocsLoading || patientDocsLoading) && (
          <LoadingSpinner size='sm' text='Loading documents...' />
        )}
      </div>
      <div>
        <div className='h-[calc(100vh-12rem)]'>
          <PDFViewerNative
            documents={[
              ...intakeDocuments.map(doc => ({
                id: doc.id,
                fileName: doc.file_name,
                tooltipText: `[Intake] ${doc.file_name}`,
                isPersisted: true,
                usedInExtraction: doc.used_in_extraction ?? true,
              })),
              ...patientDocuments.map(doc => {
                const taskTag = formatTaskTag(doc);
                return {
                  id: doc.id,
                  fileName: doc.file_name,
                  tooltipText: taskTag
                    ? `${taskTag} ${doc.file_name}`
                    : undefined,
                  isPersisted: true,
                  usedInExtraction: true,
                };
              }),
            ]}
            documentUrls={documentUrls}
            activeDocumentId={activeDocumentId}
            onActiveDocumentChange={setActiveDocumentId}
            onDocumentRename={handleDocumentRename}
            onDocumentAdd={handleDocumentAdd}
            onRefreshUrl={handleRefreshDocumentUrl}
          />
        </div>
      </div>
    </div>
  );
});

PatientDocumentsTab.displayName = 'PatientDocumentsTab';

export default PatientDocumentsTab;
